import { httpClient } from './api/httpClient';
import { isMockEnabled, mockResponse } from './api/mockAdapter';
import { API_ENDPOINTS } from 'constants/apiEndpoints';
import { STAGE1_STATUS, SOURCE } from 'constants/status';
import { MOCK_STAGE1 } from 'mock/pipeline.mock';

export const toStage1 = (raw) =>
  raw && {
    id: raw.id,
    articleReferenceId: raw.article_reference_id ?? '',
    rootArticleId: raw.root_article_id ?? '',
    currentStage: raw.current_stage ?? 'stage_one',
    workflowState: raw.workflow_state ?? 'active',
    documentVersion: raw.document_version ?? 1,
    status: raw.workflow?.status ?? raw.status ?? 'pending',
    newspaperName: raw.newspaper_name ?? raw.newspaperName ?? '',
    edition: raw.edition ?? '',
    keywords: raw.keywords ?? raw.keyword ?? [],
    keyword: raw.keywords ?? raw.keyword ?? [],
    articleTitle: raw.article_title ?? raw.article ?? '',
    article: raw.article_title ?? raw.article ?? '',
    headline: raw.headline ?? '',
    pageNumber: raw.page_number ?? raw.pageNumber ?? null,
    language: raw.language ?? null,
    articleLink: raw.article_image_url ?? raw.article_link ?? raw.articleLink ?? '',
    articleImageUrl: raw.article_image_url ?? null,
    newspaperLink: raw.newspaper_page_image_url ?? raw.newspaper_link ?? raw.newspaperLink ?? '',
    newspaperPageImageUrl: raw.newspaper_page_image_url ?? null,
    mergedPdfUrl: raw.merged_pdf_url ?? null,
    mergedPdfStatus: raw.merged_pdf_status ?? null,
    source: raw.source?.type ?? raw.source ?? '',
    isManualUpload: raw.is_manual_upload ?? false,
    previousStage: raw.previous_stage ?? null,
    workflowTimeline: raw.workflow_timeline ?? [],
    workflow: raw.workflow ?? null,
    createdAt: raw.created_at ?? raw.createdAt ?? null,
    createdBy: raw.created_by ?? raw.createdBy ?? '',
    notes: raw.notes ?? null,
  };

let store = MOCK_STAGE1.map((r) => ({ ...r }));

export const stage1Service = {
  async list(params = {}) {
    if (isMockEnabled()) {
      let filtered = store;
      if (params.newspaper_name) {
        filtered = filtered.filter(a => a.newspaper_name === params.newspaper_name || a.newspaperName === params.newspaper_name);
      }
      const items = filtered.map(toStage1);
      return { items, total: items.length, page: 1, pageSize: items.length || 20, totalPages: 1 };
    }
    const data = await httpClient.get(API_ENDPOINTS.STAGE1.LIST, { params });
    return {
      items: (data.items ?? []).map(toStage1),
      total: data.total ?? 0,
      page: data.page ?? 1,
      pageSize: data.page_size ?? 20,
      totalPages: data.total_pages ?? 0,
    };
  },

  async approve(id, documentVersion) {
    if (isMockEnabled()) {
      store = store.map((r) =>
        r.id === id
          ? {
              ...r,
              workflow: { ...r.workflow, current_stage: 'stage2', status: STAGE1_STATUS.APPROVED },
            }
          : r
      );
      return toStage1(await mockResponse(store.find((r) => r.id === id)));
    }
    return toStage1(
      await httpClient.post(API_ENDPOINTS.STAGE1.APPROVE(id), { document_version: documentVersion })
    );
  },

  async reject(id, reason, documentVersion) {
    if (isMockEnabled()) {
      store = store.map((r) =>
        r.id === id
          ? { ...r, workflow: { ...r.workflow, status: STAGE1_STATUS.REJECTED } }
          : r
      );
      return toStage1(await mockResponse(store.find((r) => r.id === id)));
    }
    return toStage1(
      await httpClient.post(API_ENDPOINTS.STAGE1.REJECT(id), { reason, document_version: documentVersion })
    );
  },

  async bulkApprove(ids) {
    if (isMockEnabled()) {
      store = store.map((r) =>
        ids.includes(r.id)
          ? {
              ...r,
              workflow: { ...r.workflow, current_stage: 'stage2', status: STAGE1_STATUS.APPROVED },
            }
          : r
      );
      return await mockResponse({ message: `Successfully approved ${ids.length} articles.` });
    }
    return await httpClient.post(API_ENDPOINTS.STAGE1.BULK_APPROVE, { ids });
  },

  async bulkReject(ids) {
    if (isMockEnabled()) {
      store = store.map((r) =>
        ids.includes(r.id)
          ? { ...r, workflow: { ...r.workflow, status: STAGE1_STATUS.REJECTED } }
          : r
      );
      return await mockResponse({ message: `Successfully rejected ${ids.length} articles.` });
    }
    return await httpClient.post(API_ENDPOINTS.STAGE1.BULK_REJECT, { ids });
  },

  async manualUpload(formData) {
    if (isMockEnabled()) {
      const fileName = formData.get('newspaper_name');
      const created = {
        id: `mock-manual-${Date.now()}`,
        article_reference_id: `KVO-MAN-${Date.now().toString().slice(-4)}`,
        root_article_id: `ROOT-${Date.now()}`,
        current_stage: 'stage_one',
        workflow_state: 'active',
        document_version: 1,
        newspaper_name: fileName || 'Mock Newspaper',
        edition: formData.get('edition') || 'Daily',
        keywords: [formData.get('keyword')],
        article_title: `Manual upload: ${formData.get('keyword') || 'Article'} (Pg ${formData.get('page_number')})`,
        headline: '',
        page_number: parseInt(formData.get('page_number') || '1', 10),
        article_image_url: 'https://picsum.photos/seed/upload-art/420/280',
        newspaper_page_image_url: 'https://picsum.photos/seed/upload/420/280',
        merged_pdf_url: null,
        merged_pdf_status: null,
        source: { type: SOURCE.MANUAL, identifier: null },
        is_manual_upload: true,
        status: STAGE1_STATUS.PENDING,
        previous_stage: null,
        workflow_timeline: [],
        created_at: new Date().toISOString(),
        created_by: 'manual',
      };
      store = [created, ...store];
      return toStage1(await mockResponse(created));
    }
    return toStage1(await httpClient.post(API_ENDPOINTS.STAGE1.MANUAL_UPLOAD, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000 // Increase timeout to 120 seconds for Google Drive file uploads
    }));
  },

  async updateArticle(id, formData) {
    if (isMockEnabled()) {
      const articleIndex = store.findIndex((r) => r.id === id);
      if (articleIndex !== -1) {
        const existing = store[articleIndex];
        const pageNumber = formData.get('page_number');
        const updated = { ...existing };
        if (pageNumber) updated.page_number = parseInt(pageNumber, 10);
        // Mock updating image URLs if files are provided
        if (formData.get('article_file')) updated.article_image_url = 'https://picsum.photos/seed/updated-art/420/280';
        if (formData.get('page_file')) updated.newspaper_page_image_url = 'https://picsum.photos/seed/updated-page/420/280';
        store[articleIndex] = updated;
        return toStage1(await mockResponse(updated));
      }
      return Promise.reject(new Error('Article not found'));
    }
    // Content-Type will be automatically set to multipart/form-data by axios when passing FormData
    return toStage1(await httpClient.patch(API_ENDPOINTS.STAGE1.DETAIL(id), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000 // Increase timeout to 120 seconds for Google Drive file uploads
    }));
  },

  async manualTelegramRun(payload) {
    if (isMockEnabled()) {
      return await mockResponse({ message: 'Telegram run started' });
    }
    const response = await httpClient.post(API_ENDPOINTS.STAGE1.MANUAL_TELEGRAM, payload);
    return response;
  },
};

export default stage1Service;
